import { getToken } from "../api";
import React, { useState } from "react";

const BASE = window.location.hostname === "localhost"
  ? "http://localhost:8001"
  : `${window.location.protocol}//${window.location.host}/api`;

const COLORS = ["#8B6914","#2ecc71","#3498db","#9b59b6","#e74c3c","#1abc9c","#e67e22","#e91e63"];

export default function Settings({ user, onUpdate }) {
  const [currentPwd, setCurrentPwd]   = useState("");
  const [newPwd, setNewPwd]           = useState("");
  const [confirmPwd, setConfirmPwd]   = useState("");
  const [selectedColor, setSelectedColor] = useState(user?.color || "#8B6914");
  const [toast, setToast]             = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew]         = useState(false);
  const [twoFAStep, setTwoFAStep]       = useState(null); // null | 'setup' | 'verify' | 'disable'
  const [twoFAQR, setTwoFAQR]           = useState("");
  const [twoFASecret, setTwoFASecret]   = useState("");
  const [twoFACode, setTwoFACode]       = useState("");
  const [backupCodes, setBackupCodes]   = useState([]);
  const [has2FA, setHas2FA]             = useState(false);

  // Detectar si el usuario ya tiene 2FA activo
  React.useEffect(() => {
    const token = getToken();
    if (!token) return;
    fetch(`${BASE}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setHas2FA(!!data.totp_enabled))
      .catch(() => {});
  }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const saveColor = async () => {
    try {
      const r = await fetch(`${BASE}/auth/update-profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user.email, color: selectedColor })
      });
      if (!r.ok) throw new Error();
      showToast("Color actualizado ✓");
      if (onUpdate) onUpdate({ ...user, color: selectedColor });
    } catch { showToast("Error al guardar"); }
  };

  const changePassword = async () => {
    if (!currentPwd || !newPwd || !confirmPwd) return showToast("Rellena todos los campos");
    if (newPwd !== confirmPwd) return showToast("Las contraseñas no coinciden");
    if (newPwd.length < 6) return showToast("Mínimo 6 caracteres");
    try {
      const r = await fetch(`${BASE}/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user.email, current_password: currentPwd, new_password: newPwd })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Error");
      showToast("Contraseña actualizada ✓");
      setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
    } catch (e) { showToast(e.message || "Error al cambiar contraseña"); }
  };

  const setup2FA = async () => {
    const token = getToken();
    try {
      const r = await fetch(`${BASE}/auth/2fa/setup`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await r.json();
      setTwoFAQR(data.qr);
      setTwoFASecret(data.secret);
      setTwoFAStep("setup");
    } catch { showToast("Error iniciando 2FA"); }
  };

  const verify2FA = async () => {
    const token = getToken();
    try {
      const r = await fetch(`${BASE}/auth/2fa/verify-setup`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ code: twoFACode })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Código incorrecto");
      setBackupCodes(data.backup_codes);
      setTwoFAStep("done");
      setHas2FA(true);
      setTwoFACode("");
      showToast("2FA activado ✓");
    } catch (e) { showToast(e.message || "Error"); }
  };

  const disable2FA = async () => {
    const token = getToken();
    try {
      const r = await fetch(`${BASE}/auth/2fa/disable`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ code: twoFACode })
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Código incorrecto");
      setHas2FA(false);
      setTwoFAStep(null);
      setTwoFACode("");
      showToast("2FA desactivado");
    } catch (e) { showToast(e.message || "Error"); }
  };

  const S = {
    wrap: { fontFamily: "Syne, sans-serif", maxWidth: 560 },
    header: { marginBottom: "2rem", borderBottom: "0.5px solid var(--border)", paddingBottom: "1.5rem" },
    title: { fontSize: 26, fontWeight: 700, color: "var(--text)", fontFamily: "Syne, sans-serif", margin: 0, letterSpacing: "-0.5px" },
    sub: { fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--gold)", marginBottom: 8 },
    section: { marginBottom: "2.5rem" },
    sectionTitle: { fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--gold)", marginBottom: "1rem", fontFamily: "Syne, sans-serif" },
    input: { width: "100%", background: "var(--lift)", border: "0.5px solid var(--border)", color: "var(--text)", padding: "10px 14px", fontFamily: "Syne, sans-serif", fontSize: 13, boxSizing: "border-box", marginBottom: 12, borderRadius: "6px", outline: "none" },
    inputWrap: { position: "relative", marginBottom: 12 },
    inputInner: { width: "100%", background: "var(--lift)", border: "0.5px solid var(--border)", color: "var(--text)", padding: "10px 40px 10px 14px", fontFamily: "Syne, sans-serif", fontSize: 13, boxSizing: "border-box", borderRadius: "6px", outline: "none" },
    eyeBtn: { position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--mute)", cursor: "pointer", fontSize: 16 },
    btn: { padding: "9px 20px", background: "var(--gold)", color: "var(--bgApp)", border: "none", fontFamily: "Syne, sans-serif", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer", fontWeight: 700, borderRadius: "5px" },
    colorGrid: { display: "flex", gap: 12, flexWrap: "wrap", marginBottom: "1rem" },
    toast: { position: "fixed", bottom: 24, right: 24, background: "var(--card)", border: "1px solid var(--goldDim)", color: "var(--gold)", padding: "10px 20px", fontFamily: "Syne, sans-serif", fontSize: 12, fontWeight: 700, zIndex: 999, borderRadius: "6px" },
    avatar: { width: 52, height: 52, borderRadius: "50%", background: selectedColor, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: "#1A1612", fontFamily: "Syne, sans-serif", marginBottom: "1rem" },
  };

  return (
    <div style={S.wrap}>
      {toast && <div style={S.toast}>{toast}</div>}
      <div style={S.header}>
        <div style={S.sub}>Mi cuenta</div>
        <h1 style={S.title}>Configuración</h1>
      </div>
      <div style={S.section}>
        <div style={S.sectionTitle}>Color de perfil</div>
        <div style={S.avatar}>{user?.avatar}</div>
        <div style={S.colorGrid}>
          {COLORS.map(c => (
            <div key={c} onClick={() => setSelectedColor(c)}
              style={{ width: 34, height: 34, borderRadius: "50%", background: c, cursor: "pointer",
                outline: selectedColor === c ? "2px solid var(--text)" : "none", outlineOffset: 2 }} />
          ))}
        </div>
        <button style={S.btn} onClick={saveColor}>Guardar color</button>
      </div>
      <div style={S.section}>
        <div style={S.sectionTitle}>Cambiar contraseña</div>
        <div style={S.inputWrap}>
          <input type={showCurrent ? "text" : "password"} placeholder="Contraseña actual" value={currentPwd} onChange={e => setCurrentPwd(e.target.value)} style={S.inputInner} />
          <button style={S.eyeBtn} onClick={() => setShowCurrent(!showCurrent)}>{showCurrent ? "🙈" : "👁"}</button>
        </div>
        <div style={S.inputWrap}>
          <input type={showNew ? "text" : "password"} placeholder="Nueva contraseña" value={newPwd} onChange={e => setNewPwd(e.target.value)} style={S.inputInner} />
          <button style={S.eyeBtn} onClick={() => setShowNew(!showNew)}>{showNew ? "🙈" : "👁"}</button>
        </div>
        <input type="password" placeholder="Confirmar nueva contraseña" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} style={S.input} />
        <button style={S.btn} onClick={changePassword}>Cambiar contraseña</button>
      </div>

      {/* ── Autenticación de dos factores ── */}
      <div style={S.section}>
        <div style={S.sectionTitle}>Autenticación de dos factores (2FA)</div>
        {!has2FA && twoFAStep === null && (
          <div>
            <p style={{ fontSize: 13, color: "var(--mute)", fontFamily: "Syne, sans-serif", marginBottom: 16, lineHeight: 1.6 }}>
              Añade una capa extra de seguridad a tu cuenta. Necesitarás Google Authenticator o cualquier app TOTP.
            </p>
            <button style={S.btn} onClick={setup2FA}>Activar 2FA</button>
          </div>
        )}

        {twoFAStep === "setup" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <p style={{ fontSize: 13, color: "var(--mute)", fontFamily: "Syne, sans-serif", lineHeight: 1.6, margin: 0 }}>
              Escanea este código QR con Google Authenticator y luego introduce el código de 6 dígitos.
            </p>
            {twoFAQR && <img src={`data:image/png;base64,${twoFAQR}`} alt="QR 2FA" style={{ width: 180, height: 180, border: "4px solid white", borderRadius: 8 }} />}
            <p style={{ fontSize: 11, color: "var(--mute)", fontFamily: "Syne, sans-serif", margin: 0 }}>
              ¿No puedes escanear? Clave manual: <code style={{ color: "var(--gold)", letterSpacing: 2 }}>{twoFASecret}</code>
            </p>
            <input
              value={twoFACode}
              onChange={e => setTwoFACode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="Código de 6 dígitos"
              maxLength={6}
              style={{ ...S.input, letterSpacing: 6, fontSize: 20, textAlign: "center", marginBottom: 0 }}
            />
            <div style={{ display: "flex", gap: 10 }}>
              <button style={{ ...S.btn, background: "none", border: "0.5px solid var(--border)", color: "var(--mute)" }} onClick={() => setTwoFAStep(null)}>Cancelar</button>
              <button style={S.btn} onClick={verify2FA} disabled={twoFACode.length !== 6}>Verificar y activar</button>
            </div>
          </div>
        )}

        {twoFAStep === "done" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <p style={{ fontSize: 13, color: "#27ae60", fontFamily: "Syne, sans-serif", margin: 0 }}>✓ 2FA activado correctamente</p>
            <p style={{ fontSize: 12, color: "var(--mute)", fontFamily: "Syne, sans-serif", margin: 0 }}>Guarda estos códigos de recuperación en un lugar seguro. Solo se muestran una vez:</p>
            <div style={{ background: "var(--lift)", border: "0.5px solid var(--border)", borderRadius: 6, padding: "12px 16px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {backupCodes.map((c, i) => (
                <code key={i} style={{ fontFamily: "monospace", fontSize: 13, color: "var(--gold)", letterSpacing: 2 }}>{c}</code>
              ))}
            </div>
            <button style={S.btn} onClick={() => setTwoFAStep(null)}>Hecho</button>
          </div>
        )}

        {has2FA && twoFAStep === null && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <p style={{ fontSize: 13, color: "#27ae60", fontFamily: "Syne, sans-serif", margin: 0 }}>✓ 2FA activo en tu cuenta</p>
            <button style={{ ...S.btn, background: "none", border: "0.5px solid #8B3A3A", color: "#8B3A3A" }} onClick={() => setTwoFAStep("disable")}>Desactivar 2FA</button>
          </div>
        )}

        {twoFAStep === "disable" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <p style={{ fontSize: 13, color: "var(--mute)", fontFamily: "Syne, sans-serif", margin: 0 }}>Introduce el código de tu app para confirmar la desactivación:</p>
            <input
              value={twoFACode}
              onChange={e => setTwoFACode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="Código de 6 dígitos"
              maxLength={6}
              style={{ ...S.input, letterSpacing: 6, fontSize: 20, textAlign: "center", marginBottom: 0 }}
            />
            <div style={{ display: "flex", gap: 10 }}>
              <button style={{ ...S.btn, background: "none", border: "0.5px solid var(--border)", color: "var(--mute)" }} onClick={() => { setTwoFAStep(null); setTwoFACode(""); }}>Cancelar</button>
              <button style={{ ...S.btn, background: "#8B3A3A", border: "none" }} onClick={disable2FA} disabled={twoFACode.length !== 6}>Confirmar desactivación</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
